import { Spacing } from '@persona-id/design-tokens';
import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import AllowedHostsList from 'dashboard/components/AllowedHostsList';
import Divider from 'dashboard/components/Divider';
import IntegrationHeroCard from 'dashboard/components/IntegrationHeroCard';
import IntegrationStep from 'dashboard/components/IntegrationStep';
import ModalWrapper from 'dashboard/components/ModalWrapper';
import TemplateSelector from 'dashboard/components/TemplateSelector/TemplateSelector';
import ConfirmationModal from 'dashboard/components-lib/ConfirmationModal';
import { AddAllowedHostFormData } from 'dashboard/forms/FormAddAllowedHost';
import FormSection from 'dashboard/layouts/FormLayout/Section';
import { Environment } from 'dashboard/models/environment';
import { InquiryTemplate } from 'dashboard/models/inquiry-template';
import { Organization } from 'dashboard/models/organization';
import { Template } from 'dashboard/models/template';
import EmbeddedCodeSnippet from 'dashboard/views/DashboardGettingStartedEmbeddedFlowView/EmbeddedCodeSnippet';
import Card from 'design-system/components/Card';
import Spacer from 'design-system/components/Spacer';
import { Paragraph } from 'design-system/components/Text';
import EmbeddedFlowImage from 'images/dashboard/getting-started/embedded-flow.svg';

const UnorderedList = styled('ul')`
  margin-left: ${Spacing[5]};
`;

const ListItem = styled('li')`
  margin: ${Spacing[2]} 0px;
  font-size: ${({ theme }) => theme.text.paragraph.md.fontSize};
  line-height: ${({ theme }) => theme.text.paragraph.md.lineHeight};
`;

interface DashboardGettingStartedEmbeddedFlowViewProps {
  currentEnvironment: Environment;
  organization: Organization;
  selectedTemplate: Template | InquiryTemplate | undefined;
  templates: Array<Template | InquiryTemplate>;

  addAllowedHost: (values: AddAllowedHostFormData) => Promise<void>;
  handleTemplateSelect: (templateId: string) => void;
  removeAllowedHost: (hostIndex: number) => Promise<void>;
}

const DashboardGettingStartedEmbeddedFlowView = ({
  addAllowedHost,
  currentEnvironment,
  handleTemplateSelect,
  organization,
  removeAllowedHost,
  selectedTemplate,
  templates,
}: DashboardGettingStartedEmbeddedFlowViewProps) => {
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const [hostToRemoveIndex, setHostToRemoveIndex] = useState<number>(undefined);

  const hostToRemove =
    organization && hostToRemoveIndex != null
      ? organization.attributes['allowed-hosts'][hostToRemoveIndex]
      : null;

  const handleRemoveAllowedHost = (hostIndex: number) => {
    setHostToRemoveIndex(hostIndex);
  };

  const handleConfirmRemoveAllowedHost = () => removeAllowedHost(hostToRemoveIndex);
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const hideConfirmRemoveHostModal = useCallback(() => setHostToRemoveIndex(undefined), []);

  return (
    <>
      <IntegrationHeroCard title='Embedded Flow' image={EmbeddedFlowImage}>
        With the embedded flow (React SDK) integration method, you can embed an information
        collection experience and parallel Verifications (configurable through an Inquiry Template)
        in your web application, and dynamically create an individual Inquiry to later review the
        associated information collected and verification results.
      </IntegrationHeroCard>
      <Card>
        <Card.Body>
          <Card.Title>How to use this method</Card.Title>
          <IntegrationStep title='Select your Inquiry Template' stepNumber={1}>
            <Paragraph color='primary'>
              Select the Inquiry Template you would like to use from the dropdown below. The Inquiry
              Template determines what information is collected and which Verifications you’d like
              to run on the data that is collected.
            </Paragraph>
            <Spacer height={1} />
            <Paragraph color='primary'>
              If you would like to create a new Inquiry Template (and you subscribe to a plan that
              allows you to create your own Inquiry Templates), open the{' '}
              <Link to='/dashboard/inquiry-templates' target='_blank'>
                Inquiry Templates
              </Link>{' '}
              page in your Dashboard and then come back to this page.
            </Paragraph>
            <Spacer height={1} />
            {templates.length >= 1 && selectedTemplate ? (
              <TemplateSelector
                handleTemplateSelect={handleTemplateSelect}
                templates={templates}
                value={selectedTemplate.id}
                label='Inquiry Template'
              />
            ) : (
              <Paragraph>Loading... try refreshing the page!</Paragraph>
            )}
          </IntegrationStep>
          <Divider full />
          <IntegrationStep title='Embed the flow' stepNumber={2}>
            <Paragraph color='primary'>
              Paste the embedded flow code snippet below into your code, on the page you’d like to
              trigger the Inquiry experience.
            </Paragraph>
            <Spacer height={1} />
            <Paragraph color='primary'>
              Make sure to set the{' '}
              <b>
                <code>environment</code>
              </b>{' '}
              properly! Use <code>sandbox</code> when testing, as this will return dummy data, save
              data to your sandbox environment, and usage will not count toward billing. Use{' '}
              <code>production</code> for live Inquires.
            </Paragraph>
            <Spacer height={2} />
            {selectedTemplate && (
              <EmbeddedCodeSnippet
                id='getting-started__embedded-flow-snippet'
                template={selectedTemplate}
                environment={currentEnvironment}
              />
            )}
            <Spacer height={2} />
            <Paragraph color='primary'>
              This code snippet will open the embedded flow as soon as it loads. You can learn more
              about the client’s methods to show or hide the embedded flow{' '}
              <a
                href='https://docs.withpersona.com/docs/embedded-flow-client-methods'
                target='_blank'
                rel='noreferrer'
              >
                here
              </a>
              .
            </Paragraph>
          </IntegrationStep>
          <Divider full />
          <IntegrationStep title='Configure allowed domains' stepNumber={3} isRecommendedOnly>
            <Paragraph color='primary'>
              Because the embedded flow loads an iframe that contains the Inquiry experience, we
              recommend that you configure allowed domains. You can update this below.
            </Paragraph>
            <Spacer height={1} />
            <Paragraph color='primary'>
              A wildcard character (*) can be used to specify subdomains. If this list is empty, all
              domains are allowed. Otherwise, Persona will only allow redirects or embeds for
              domains in this list. The domain allowlist is shared across <b>all environments</b> in
              your organization. Read more about the domain allowlist{' '}
              <a
                href='https://docs.withpersona.com/docs/hosted-flow-security#allowed-domains'
                target='_blank'
                rel='noreferrer'
              >
                here
              </a>
              .
            </Paragraph>
            <Spacer height={3} />
            <FormSection data-test='getting-started__allowed-hosts-list'>
              <AllowedHostsList
                allowedHosts={organization.attributes['allowed-hosts']}
                addAllowedHost={addAllowedHost}
                removeAllowedHost={handleRemoveAllowedHost}
              />
            </FormSection>
          </IntegrationStep>
          <Divider full />
          <IntegrationStep title='Optimize' stepNumber={4}>
            <Paragraph color='primary'>
              That’s it — congrats! Now you want to test the experience and see if you would like to
              do any additional optimizations. Some common optimizations to consider include:
            </Paragraph>
            <UnorderedList>
              <ListItem>
                Pre-creating inquiries via the API and resuming them in the React SDK
              </ListItem>
              <ListItem>Using optional callbacks for advanced event handling</ListItem>
              <ListItem>Retrieving information about each Inquiry via the API</ListItem>
              <ListItem>
                Specifying your user ID as the Persona Reference ID so you can link all Inquiries
                from one individual to a Persona Account
              </ListItem>
              <ListItem>
                Using the <code>fields</code> parameter to dynamically present Inquiry field values
                such as name or birthday so your users just need to confirm that those values are
                correct
              </ListItem>
              <ListItem>
                <Link to='/dashboard/inquiry-manage-domain'>
                  Restricting the URI schemes that are allowed
                </Link>{' '}
                to load the embedded flow
              </ListItem>
              <ListItem>... and more!</ListItem>
            </UnorderedList>
            <Paragraph color='primary'>
              Learn about these optimizations{' '}
              <a
                href='https://docs.withpersona.com/docs/embedded-flow-fields'
                target='_blank'
                rel='noreferrer'
              >
                here
              </a>
              .
            </Paragraph>
          </IntegrationStep>
        </Card.Body>
      </Card>

      <ModalWrapper isOpen={hostToRemove != null} handleCloseModal={hideConfirmRemoveHostModal}>
        <ConfirmationModal
          cancelAction={hideConfirmRemoveHostModal}
          confirmAction={handleConfirmRemoveAllowedHost}
          header='Remove allowed domain?'
          confirmActionText='Remove domain'
          cancelActionText='Cancel'
        >
          Once <b>{hostToRemove}</b> is removed, customers in the middle of completing verifications
          on this domain will be unable to finish. <b>This affects all environments.</b>
        </ConfirmationModal>
      </ModalWrapper>
    </>
  );
};

export default DashboardGettingStartedEmbeddedFlowView;
